import { useCallback, useMemo, useState } from 'react';
import {
  CASE_PLAN_SECTION_STATUSES,
  DEFAULT_CASE_PLAN_SECTION_TAB,
} from '../../constants/reentry-planning.constants';
import { filterSectionsForTab } from '../../utils/case-plan-section.util';

/**
 * Owns the Active/Archived tab state of the section list.
 *
 * The tab is deliberately never reset by a mutation: archiving or deleting a section keeps the
 * admin on Active, where the section simply disappears from the list. An archived section
 * moves to the Archived tab; a deleted one is on neither tab, and the Active count and the
 * export selection both follow from the same split.
 *
 * @param {object[]} sections - All sections of the plan, active and archived.
 * @returns {{
 *   activeSectionTab: string,
 *   onSectionTabChange: (event: object, value: string) => void,
 *   activeSections: object[],
 *   visibleSections: object[],
 *   activeSectionCount: number,
 *   isArchivedSectionTab: boolean,
 * }}
 */
export const useCasePlanSectionTabs = (sections = []) => {
  const [activeSectionTab, setActiveSectionTab] = useState(DEFAULT_CASE_PLAN_SECTION_TAB.value);

  const onSectionTabChange = useCallback((_event, value) => {
    setActiveSectionTab(value);
  }, []);

  const activeSections = useMemo(
    () => filterSectionsForTab(sections, CASE_PLAN_SECTION_STATUSES.ACTIVE),
    [sections],
  );

  const archivedSections = useMemo(
    () => filterSectionsForTab(sections, CASE_PLAN_SECTION_STATUSES.ARCHIVED),
    [sections],
  );

  const isArchivedSectionTab = activeSectionTab === CASE_PLAN_SECTION_STATUSES.ARCHIVED;

  return {
    activeSectionTab,
    onSectionTabChange,
    activeSections,
    visibleSections: isArchivedSectionTab ? archivedSections : activeSections,
    activeSectionCount: activeSections.length,
    isArchivedSectionTab,
  };
};
